import { create } from 'zustand';
import {
  activePlayer,
  canUndo,
  chooseAction,
  createGame,
  filterEvents,
  getLegalActions,
  getPlayerView,
  replay,
  Rng,
  tryApplyAction,
  type Action,
  type CombatSnapshot,
  type GameConfig,
  type GameEvent,
  type GameState,
  type PlayerId,
  type PlayerView,
} from '@estandarte/engine';
import type { GameSeat } from '@estandarte/protocol';
import { describeEvent } from '../lib/describe';
import { readJson, removeKey, SAVE_KEY, SETTINGS_KEY, writeJson } from '../lib/storage';
import { chronicleOf, errorText } from '../theme';
import { busyMs, resetPace } from '../lib/pace';

export type Screen = 'home' | 'localSetup' | 'game' | 'create' | 'join' | 'lobby' | 'about';

export type CombatEvent = Extract<GameEvent, { type: 'combat' | 'bossCombat' | 'combatPreview' }>;

function findCombat(events: GameEvent[]): CombatEvent | null {
  for (let i = events.length - 1; i >= 0; i--) {
    const e = events[i];
    if (e.type === 'combat' || e.type === 'bossCombat' || e.type === 'combatPreview') return e;
  }
  return null;
}

/** Campos do store derivados de eventos novos. */
function combatPatch(events: GameEvent[], st: { combatSeq: number }) {
  const c = findCombat(events);
  const roll = [...events].reverse().find((e) => e.type === 'bossRoll');
  return {
    ...(c ? { lastCombat: c, combatSeq: st.combatSeq + 1 } : {}),
    ...(roll?.type === 'bossRoll' ? { lastBossRoll: roll.roll } : {}),
  };
}

export interface LogEntry {
  id: number;
  text: string;
  kind: GameEvent['type'];
}

interface SavedGame {
  version: 1;
  config: GameConfig;
  actions: Action[];
}

interface Settings {
  fastBots: boolean;
}

export interface GameStore {
  screen: Screen;
  mode: 'local' | 'online' | null;
  view: PlayerView | null;
  /** Lugar de quem está olhando (null = espectador, ex.: entre turnos no hot-seat). */
  me: PlayerId | null;
  legal: Action[];
  log: LogEntry[];
  /** Eventos da última ação, para animar dados e conquistas. */
  lastEvents: GameEvent[];
  eventSeq: number;
  /** Último combate (para o painel de dados). */
  lastCombat: CombatEvent | null;
  combatSeq: number;
  /** Última face do dado do boss (início de rodada). */
  lastBossRoll: number | null;
  error: string | null;
  fastBots: boolean;
  /** Hot-seat: o humano ativo precisa confirmar que está na frente da tela. */
  needsPass: PlayerId | null;
  canUndo: boolean;
  /** Janela do Amuleto da Sorte aberta para um humano (quem decide e a rolagem). */
  reaction: { player: PlayerId; role: 'attacker' | 'defender'; combat: CombatSnapshot } | null;
  onlineSeats: GameSeat[];
  isHost: boolean;

  go(screen: Screen): void;
  setError(error: string | null): void;
  setFastBots(on: boolean): void;
  dispatch(action: Action): void;
  // local
  startLocal(config: GameConfig): void;
  hasSave(): boolean;
  loadSave(): boolean;
  undo(): void;
  confirmPass(): void;
  leaveGame(): void;
  // online
  receiveOnline(view: PlayerView, me: PlayerId, events: GameEvent[], seats: GameSeat[], host: boolean): void;
  setOnlineSender(send: ((a: Action) => void) | null): void;
}

// Estado completo da partida local (fora do store para não ir parar na UI por engano).
let local: { config: GameConfig; actions: Action[]; state: GameState } | null = null;
let revealedFor: PlayerId | null = null;
let botTimer: ReturnType<typeof setTimeout> | null = null;
let onlineSend: ((a: Action) => void) | null = null;
let logId = 0;

/** Pausa mínima entre jogadas de bot (além de esperar os sons da jogada anterior). */
const BOT_DELAY = 900;
const FAST_BOT_DELAY = 250;

function humansOf(state: GameState): PlayerId[] {
  return state.players.filter((p) => p.kind === 'human').map((p) => p.id);
}

/** Verso do Velho Bardo que abre a partida, conforme a era. */
function bardLine(era: string | null | undefined): LogEntry[] {
  const c = chronicleOf(era);
  return c ? [{ id: ++logId, text: `O Velho Bardo afina o alaúde (${c.title}): “${c.verse}”`, kind: 'setupDone' }] : [];
}

function toLog(events: GameEvent[], players: { name: string; color?: string }[]): LogEntry[] {
  const out: LogEntry[] = [];
  for (const e of events) {
    const text = describeEvent(e, players);
    if (text) out.push({ id: ++logId, text, kind: e.type });
  }
  return out;
}

export const useGame = create<GameStore>((set, get) => {
  /** Recalcula a visão local: de quem é a perspectiva, ações legais, tela de passagem. */
  function refreshLocal(newEvents: GameEvent[] | null) {
    if (!local) return;
    const s = local.state;
    const humans = humansOf(s);
    const active = activePlayer(s);
    const reaction = s.pending?.kind === 'reaction' && s.players[s.pending.player].kind === 'human' ? s.pending : null;
    // Hot-seat: sempre que outro jogador assume, o próximo humano precisa passar pela tela de passagem.
    // A janela do amuleto é rápida: não troca de tela, só pergunta a quem decide.
    if (active !== revealedFor && !reaction) revealedFor = null;
    const hotSeat = humans.length > 1;
    let me: PlayerId | null;
    let needsPass: PlayerId | null = null;
    if (s.phase === 'over') me = hotSeat ? null : humans[0] ?? null;
    else if (reaction && hotSeat) me = revealedFor;
    else if (active !== null && s.players[active].kind === 'human') {
      if (!hotSeat) me = active;
      else if (revealedFor === active) me = active;
      else {
        me = null;
        needsPass = active;
      }
    } else me = hotSeat ? null : humans[0] ?? null;
    const view = getPlayerView(s, me);
    const visibleEvents = newEvents ? filterEvents(newEvents, hotSeat ? null : me) : [];
    const myTurn = me !== null && active === me;
    set((st) => ({
      view,
      me,
      needsPass,
      reaction: reaction ? { player: reaction.player, role: reaction.role, combat: reaction.combat } : null,
      legal: myTurn ? getLegalActions(s, me!) : [],
      canUndo: myTurn && canUndo(s, me!),
      log: newEvents ? [...st.log, ...toLog(visibleEvents, s.players)].slice(-300) : st.log,
      lastEvents: newEvents ? visibleEvents : st.lastEvents,
      eventSeq: newEvents ? st.eventSeq + 1 : st.eventSeq,
      ...combatPatch(visibleEvents, st),
    }));
  }

  function save() {
    if (!local) return;
    const data: SavedGame = { version: 1, config: local.config, actions: local.actions };
    if (local.state.phase === 'over') removeKey(SAVE_KEY);
    else writeJson(SAVE_KEY, data);
  }

  function clearBot() {
    if (botTimer) clearTimeout(botTimer);
    botTimer = null;
  }

  function scheduleBot() {
    clearBot();
    if (!local) return;
    const s = local.state;
    const active = activePlayer(s);
    if (active === null || s.players[active].kind !== 'bot') return;
    const delay = get().fastBots ? FAST_BOT_DELAY : BOT_DELAY;
    const run = () => {
      botTimer = null;
      if (!local) return;
      // Sons e animações da última ação ainda rolando: espera terminarem.
      const wait = busyMs();
      if (wait > 40) {
        botTimer = setTimeout(run, wait);
        return;
      }
      const st = local.state;
      const p = activePlayer(st);
      if (p === null || st.players[p].kind !== 'bot') return;
      const rng = new Rng((local.config.seed ^ Math.imul(local.actions.length + 1, 2654435761)) | 0);
      const action = chooseAction(getPlayerView(st, p), getLegalActions(st, p), rng);
      applyLocal(action);
    };
    botTimer = setTimeout(run, delay);
  }

  function applyLocal(action: Action) {
    if (!local) return;
    const res = tryApplyAction(local.state, action);
    if (!res.ok) {
      set({ error: errorText(res.error) });
      return;
    }
    local = { ...local, state: res.state, actions: [...local.actions, action] };
    set({ error: null });
    save();
    refreshLocal(res.state.events);
    scheduleBot();
  }

  const settings = readJson<Settings>(SETTINGS_KEY);

  return {
    screen: 'home',
    mode: null,
    view: null,
    me: null,
    legal: [],
    log: [],
    lastEvents: [],
    eventSeq: 0,
    lastCombat: null,
    combatSeq: 0,
    lastBossRoll: null,
    error: null,
    fastBots: settings?.fastBots ?? false,
    needsPass: null,
    canUndo: false,
    reaction: null,
    onlineSeats: [],
    isHost: false,

    go: (screen) => set({ screen, error: null }),
    setError: (error) => set({ error }),
    setFastBots: (fastBots) => {
      set({ fastBots });
      writeJson(SETTINGS_KEY, { fastBots });
    },

    dispatch(action) {
      const { mode } = get();
      if (mode === 'local') applyLocal(action);
      else if (mode === 'online') onlineSend?.(action);
    },

    startLocal(config) {
      clearBot();
      resetPace();
      local = { config, actions: [], state: createGame(config) };
      revealedFor = null;
      logId = 0;
      set({ mode: 'local', screen: 'game', log: bardLine(config.ruleset?.era), lastEvents: [], lastCombat: null, error: null });
      save();
      refreshLocal([]);
      scheduleBot();
    },

    hasSave: () => !!readJson<SavedGame>(SAVE_KEY)?.config,

    loadSave() {
      const saved = readJson<SavedGame>(SAVE_KEY);
      if (!saved?.config || !Array.isArray(saved.actions)) return false;
      try {
        clearBot();
        const state = replay(saved.config, saved.actions);
        local = { config: saved.config, actions: saved.actions, state };
        revealedFor = null;
        logId = 0;
        set({
          mode: 'local',
          screen: 'game',
          log: [{ id: ++logId, text: `Partida carregada (rodada ${state.round}).`, kind: 'roundStart' }],
          lastEvents: [],
          error: null,
        });
        refreshLocal(null);
        scheduleBot();
        return true;
      } catch {
        removeKey(SAVE_KEY);
        set({ error: 'Não foi possível carregar a partida salva.' });
        return false;
      }
    },

    undo() {
      if (!local || get().mode !== 'local') return;
      const me = get().me;
      if (me === null || !canUndo(local.state, me)) return;
      clearBot();
      const actions = local.actions.slice(0, -1);
      local = { ...local, actions, state: replay(local.config, actions) };
      save();
      set((st) => ({ log: [...st.log, { id: ++logId, text: 'Ação desfeita.', kind: 'phase' }] }));
      refreshLocal(null);
      scheduleBot();
    },

    confirmPass() {
      if (!local) return;
      revealedFor = activePlayer(local.state);
      refreshLocal(null);
    },

    leaveGame() {
      clearBot();
      resetPace();
      local = null;
      revealedFor = null;
      set({ mode: null, view: null, me: null, legal: [], screen: 'home', log: [], needsPass: null, lastEvents: [], lastCombat: null, reaction: null });
    },

    receiveOnline(view, me, events, seats, host) {
      const myTurn = activePlayer(view) === me;
      const p = view.pending;
      set((st) => ({
        reaction: p?.kind === 'reaction' && p.player === me ? { player: p.player, role: p.role, combat: p.combat } : null,
        mode: 'online',
        screen: 'game',
        view,
        me,
        isHost: host,
        onlineSeats: seats,
        needsPass: null,
        canUndo: false,
        legal: myTurn ? getLegalActions(view, me) : [],
        log: (() => {
          const base = st.log.length ? st.log : bardLine(view.info.ruleset.era);
          return events.length ? [...base, ...toLog(events, view.players)].slice(-300) : base;
        })(),
        lastEvents: events.length ? events : st.lastEvents,
        eventSeq: events.length ? st.eventSeq + 1 : st.eventSeq,
        ...combatPatch(events, st),
      }));
    },

    setOnlineSender(send) {
      onlineSend = send;
    },
  };
});

/** Só para depuração/testes: estado completo local. */
export function debugLocalState(): GameState | null {
  return local?.state ?? null;
}

// Gancho de desenvolvimento (só no `npm run dev`): dá cartas de loot a um jogador
// para testar a interface. Não entra no build de produção.
if (import.meta.env.DEV && typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).__estandarteDebug = {
    giveLoot(player: number, kinds: string[]) {
      if (!local) return;
      local.state.players[player].loot = kinds as GameState['players'][number]['loot'];
      useGame.setState({ view: getPlayerView(local.state, useGame.getState().me) });
      const me = useGame.getState().me;
      if (me !== null && activePlayer(local.state) === me) useGame.setState({ legal: getLegalActions(local.state, me) });
    },
    state: () => local?.state,
  };
}
